import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { OnboardingCallStatus, Organization } from "@front/m19-api-client";
import { OrganizationAccountGroups } from "@front/m19-models";
import { OrganizationAccountGroupService, OrganizationService } from "@front/m19-services";
import { IButtonDirective, ToasterService } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { OnboardingService } from "@m19-board/services/onboarding.service";
import { DateTime } from "luxon";
import { combineLatest, filter, take } from "rxjs";
import { environment } from "../../environments/environment";

@Component({
  selector: "app-onboard-call-page",
  template: ` <div class="flex flex-col items-center gap-3" *transloco="let t">
    <div>
      @if (onboardingCallStatus() !== OnboardingCallStatus.SCHEDULED) {
        <span class="text-xs text-gray-400">3/3</span>
        <div class="mb-2 text-xl font-semibold text-gray-700">
          {{ t("onboarding-call.your_free_onboarding_call") }}
        </div>
        <p>{{ t("onboarding-call.description") }}</p>
      } @else {
        <div class="mb-2 text-xl font-semibold text-gray-700">
          {{ t("onboarding-call.rechedule_your_call") }}
        </div>
      }
    </div>
    <div class="meetings-iframe-container min-h-[700px] w-screen" [attr.data-src]="onboardingMeetingUrl"></div>
    @if (meetingBooked()) {
      <button iButton size="md" (click)="continue()">{{ t("common.continue") }}</button>
    } @else {
      @if (onboardingCallStatus() !== OnboardingCallStatus.SCHEDULED) {
        <a class="cursor-pointer text-gray-400 hover:text-gray-500" (click)="skipOnboarding()">{{
          t("onboarding-call.skip")
        }}</a>
      } @else {
        <a class="cursor-pointer text-gray-400 hover:text-gray-500" (click)="continue()">{{
          t("common.back_to_home")
        }}</a>
      }
    }
  </div>`,
  imports: [TranslocoDirective, IButtonDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingCallPageComponent implements OnInit {
  private readonly onboardingService = inject(OnboardingService);
  private readonly router = inject(Router);
  private readonly organizationAccountGroupService = inject(OrganizationAccountGroupService);
  private readonly organizationService = inject(OrganizationService);
  private readonly toastrService = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);
  readonly meetingBooked = signal(false);
  readonly meetingDate = signal<string | undefined>(undefined);
  readonly onboardingMeetingUrl = environment.onboardingMeetingUrl;
  private organizationId: number | undefined;
  readonly onboardingCallStatus = signal<OnboardingCallStatus | undefined>(undefined);
  readonly OnboardingCallStatus = OnboardingCallStatus;

  constructor() {
    // check if user is entitled to onboarding call
    combineLatest([
      this.organizationAccountGroupService.allOrganizationAccountGroups$.pipe(
        filter((o): o is OrganizationAccountGroups[] => !!o),
      ),
      this.onboardingService.belowFreeTestAdSpendThreshold$,
    ])
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(([organizationAccountGroups, belowFreeTestAdSpendThreshold]) => {
        if (organizationAccountGroups.length !== 1) {
          // free test activation is not possible in this case
          this.router.navigate(["/dashboard"]);
        }
        if (!this.onboardingService.entitledForFreeTestSubscription(organizationAccountGroups[0])) {
          // free test activation is not possible in this case
          this.router.navigate(["/dashboard"]);
        }
        if (belowFreeTestAdSpendThreshold) {
          // onboarding call is not possible in this case
          this.router.navigate(["/dashboard"]);
        }
        this.organizationId = organizationAccountGroups[0].organization.organizationId;
        this.onboardingCallStatus.set(organizationAccountGroups[0].organization.onboardingCallStatus);
      });
  }

  ngOnInit(): void {
    const myScriptElement = document.createElement("script");
    myScriptElement.src = "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
    document.body.appendChild(myScriptElement);

    // Listen for the message event to see successful scheduling
    window.addEventListener("message", this.meetingBookHandler);
  }

  private readonly meetingBookHandler = (event: MessageEvent) => {
    // Check if the message is from the scheduling widget
    if (event.origin === "https://meetings.hubspot.com") {
      // Get the message data
      const messageData = event.data;
      if (messageData.meetingBookSucceeded) {
        this.meetingBooked.set(true);
        let onboardingEpoch: string | undefined = undefined;
        onboardingEpoch = messageData?.meetingsPayload?.bookingResponse?.event.dateTime;

        window.removeEventListener("message", this.meetingBookHandler);
        this.organizationService
          .updateOnboardingCallStatus(this.organizationId!, OnboardingCallStatus.SCHEDULED, onboardingEpoch)
          .subscribe({
            next: (response: Organization[]) => {
              const organization = response.find((org) => org.organizationId === this.organizationId!);
              this.meetingDate.set(organization!.onboardingCallDatetime);
              this.onboardingService.onboardingCallDatetimeUpdate.next(
                DateTime.fromISO(organization!.onboardingCallDatetime!, { zone: "utc" })!.toLocal().toISO()!,
              );
              this.toastrService.success("Onboarding call scheduled successfully");
            },
            error: (err) => {
              this.toastrService.error(err, "Error when scheduling your onboarding call");
            },
          });
      }
    }
  };

  continue() {
    window.removeEventListener("message", this.meetingBookHandler);
    this.onboardingService.currentStep$.pipe(take(1)).subscribe(() => {
      this.router.navigate(["dashboard/home"]);
    });
  }

  skipOnboarding() {
    this.onboardingService.skipOnboarding();
    this.continue();
  }
}
